import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';
import './RemediateModal.css';

// step: 'confirm' | 'sending' | 'done' | 'error'
function RemediateModal({ alert, onClose, onDone }) {
  const { t } = useTranslation();
  const [step, setStep] = useState('confirm');

  // Notify only the real owners of the affected resources — never fabricate recipients.
  const recipients = Array.isArray(alert.affectedResources) ? alert.affectedResources : [];
  const hasRecipients = recipients.length > 0;

  const handleSend = async () => {
    if (!hasRecipients) return;
    setStep('sending');
    try {
      await axiosInstance.post('/remediation/notify', {
        alertId:    alert.auditProgressId,
        testName:   alert.testName,
        message:    alert.message,
        recipients,
      });
      setStep('done');
    } catch {
      setStep('error');
    }
  };

  const handleDone = () => {
    onDone(alert.auditProgressId);
  };

  return (
    <div className="rm-overlay" onClick={step === 'sending' ? undefined : onClose}>
      <div className="rm-modal" onClick={e => e.stopPropagation()}>
        {step === 'confirm' && (
          <>
            <div className="rm-header">
              <span className="rm-icon">🔔</span>
              <h3 className="rm-title">{t('RemediateModal.title')}</h3>
            </div>
            <p className="rm-desc">
              {t('RemediateModal.description')}
            </p>
            <div className="rm-alert-box">
              <span className="rm-alert-msg">{alert.message}</span>
              {alert.tscCode && <span className="rm-tsc">{alert.tscCode}</span>}
            </div>

            {hasRecipients ? (
              <div className="rm-recipients">
                <span className="rm-rec-label">{t('RemediateModal.recipientsLabel', { count: recipients.length })}</span>
                <ul className="rm-rec-list">
                  {recipients.map((r, i) => (
                    <li key={i} className="rm-rec-item">
                      <span className="rm-rec-avatar">{String(r)[0]?.toUpperCase()}</span>
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="rm-empty">
                <span className="rm-empty-icon">👥</span>
                <p className="rm-empty-text">
                  {t('RemediateModal.noRecipients', 'No affected resource owners are available to notify for this test yet. Assign an owner to the affected assets to enable remediation notifications.')}
                </p>
              </div>
            )}

            <div className="rm-actions">
              <button className="rm-btn-cancel" onClick={onClose}>{t('RemediateModal.cancel')}</button>
              <button className="rm-btn-send" onClick={handleSend} disabled={!hasRecipients}>
                {t('RemediateModal.sendButton')}
              </button>
            </div>
          </>
        )}

        {step === 'sending' && (
          <div className="rm-sending">
            <div className="rm-spinner" />
            <p className="rm-sending-text">{t('RemediateModal.sending')}</p>
          </div>
        )}

        {step === 'done' && (
          <div className="rm-done">
            <div className="rm-check">✓</div>
            <h3 className="rm-done-title">{t('RemediateModal.doneTitle')}</h3>
            <p className="rm-done-desc">
              {t('RemediateModal.doneDescription', { count: recipients.length })}
            </p>
            <ul className="rm-sent-list">
              {recipients.map((r, i) => (
                <li key={i} className="rm-sent-item">
                  <span className="rm-sent-check">✓</span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
            <button className="rm-btn-done" onClick={handleDone}>{t('RemediateModal.done')}</button>
          </div>
        )}

        {step === 'error' && (
          <div className="rm-done">
            <div className="rm-check" style={{ background: '#fef2f2', color: '#dc2626' }}>!</div>
            <h3 className="rm-done-title">{t('RemediateModal.errorTitle', 'Notification failed')}</h3>
            <p className="rm-done-desc">
              {t('RemediateModal.errorDescription', 'We could not send the remediation notification. Please try again or check the notification integration in Settings.')}
            </p>
            <div className="rm-actions">
              <button className="rm-btn-cancel" onClick={onClose}>{t('RemediateModal.cancel')}</button>
              <button className="rm-btn-send" onClick={() => setStep('confirm')}>{t('RemediateModal.retry', 'Retry')}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RemediateModal;
